import { useCallback, useEffect, useMemo, useState } from 'react';
// festAPI nur wegen qrUrl: der QR-Endpunkt kodiert beliebigen Text und ist
// öffentlich — kein eigener zweiter QR-Dienst nötig.
import { funnelsAPI, festAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Filter, ExternalLink, RefreshCw, TrendingDown, Phone, QrCode,
  Loader2, Copy, Check, Users, CheckCircle2, Percent,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import KidsLaunchBlock from '@/components/KidsLaunchBlock';

/* Seite "Funnels" — die beiden öffentlichen Probetrainings-Strecken.
 *
 * Beantwortet drei Fragen:
 *   1. Wo sind die Seiten?      -> Direktlink + QR zum Aushängen
 *   2. Wie gut laufen sie?      -> Starts, Abschlüsse, Conversion
 *   3. Wo springen Leute ab?    -> Balken je Schritt, größter Absprung markiert
 *
 * Dazu die Abbrecher mit Telefonnummer: wer Nummer und Sportart hinterlassen
 * hat, aber keinen Termin gewählt hat, ist der wärmste Lead im System.
 */

const FUNNELS = [
  { slug: 'kids', pfad: '/funnelkids', label: 'Kids', beschreibung: 'Eltern buchen für ihr Kind' },
  { slug: 'kampfsport', pfad: '/funnel', label: 'Erwachsene', beschreibung: 'Kampfsport & Fitness' },
  { slug: 'frauen', pfad: '/frauen', label: 'Frauen', beschreibung: 'Boxen, Kraft & Zirkel' },
  // Kampagnenseite mit Formular statt Schritt-Funnel: eigene Auswertung (components/KidsLaunchBlock.js)
  { slug: 'kids-launch', pfad: '/10wochen', label: 'Kids Launch', beschreibung: '10-Wochen-Seite', kampagne: true },
];

const ZEITRAEUME = [[7, '7 Tage'], [30, '30 Tage'], [90, '90 Tage']];

function fmtZeit(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE',
      { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/* ------------------------------------------------- Ein Funnel im Detail */

function FunnelBlock({ funnel, tage }) {
  const [stat, setStat] = useState(null);
  const [abbrecher, setAbbrecher] = useState([]);
  const [laden, setLaden] = useState(true);
  const [kopiert, setKopiert] = useState(false);
  const [qrOffen, setQrOffen] = useState(false);
  /* Auswertung (25.09.2026): Version (alte 10-Schritt-Sitzungen getrennt
     von den neuen 4) und Quelle (utm_source) als Filter */
  const [version, setVersion] = useState('');
  const [quelle, setQuelle] = useState('');

  const url = `${window.location.origin}${funnel.pfad}`;

  const holen = useCallback(async () => {
    setLaden(true);
    try {
      const [s, a] = await Promise.all([
        funnelsAPI.statistik(funnel.slug, tage, { version: version || undefined, quelle: quelle || undefined }),
        funnelsAPI.abbrecher(funnel.slug, 50).catch(() => ({ data: { sitzungen: [] } })),
      ]);
      setStat(s.data);
      setAbbrecher(a.data.sitzungen || []);
    } catch (e) {
      toast.error(`Zahlen für "${funnel.label}" konnten nicht geladen werden.`);
      setStat(null);
    }
    setLaden(false);
  }, [funnel.slug, funnel.label, tage, version, quelle]);

  useEffect(() => { holen(); }, [holen]);

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch { /* Zwischenablage verweigert */ }
  };

  if (laden) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-10">
        <Loader2 className="h-4 w-4 animate-spin" /> {funnel.label} wird geladen …
      </div>
    );
  }
  if (!stat) return null;

  const maxAbsprung = stat.groesster_absprung;

  return (
    <div className="space-y-5">
      {/* Direktzugriff */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Direktzugriff</CardTitle>
            <p className="text-sm text-muted-foreground mt-0.5 break-all">{url}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={kopieren}>
              {kopiert ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
              {kopiert ? 'Kopiert' : 'Link kopieren'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setQrOffen((o) => !o)}>
              <QrCode className="h-4 w-4 mr-1.5" /> QR-Code
            </Button>
            <Button size="sm" asChild>
              <a href={funnel.pfad} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Seite öffnen
              </a>
            </Button>
          </div>
        </CardHeader>
        {qrOffen && (
          <CardContent className="pt-0">
            <div className="flex flex-col items-center gap-2 py-2">
              <img src={festAPI.qrUrl(url)} alt={`QR-Code ${funnel.label}`}
                   width={190} height={190}
                   className="bg-white p-3 rounded-xl" />
              <p className="text-xs text-muted-foreground">
                Rechtsklick → Bild speichern, für Flyer und Aushang
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Kennzahlen */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Gestartet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stat.starts}</div>
            <p className="text-xs text-muted-foreground mt-1">
              haben die erste Frage gesehen
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Termin gebucht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stat.abgeschlossen}</div>
            <p className="text-xs text-muted-foreground mt-1">bis zum Ende durchgelaufen</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Percent className="h-4 w-4" /> Conversion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-marke">{stat.gesamt_conversion} %</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stat.starts === 0 ? 'noch keine Daten im Zeitraum' : 'von Start bis Buchung'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Schritt-für-Schritt */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Wie weit kommen die Interessenten?</CardTitle>
              <p className="text-sm text-muted-foreground">
                Ein Schritt zählt als erreicht, sobald er angezeigt wurde — die Zahlen fallen
                deshalb nur. Je Schritt: Anzahl · Anteil vom Start · Verlust zum Vorschritt.
                Interne Aufrufe (eingeloggt, ?test=1) sind nicht enthalten.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {stat.versionen?.length > 1 && (
                <select value={version || stat.version} onChange={(e) => setVersion(e.target.value)}
                        className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="funnel-version">
                  {stat.versionen.map((v) => (
                    <option key={v.version} value={v.version}>
                      Version {v.version}{v.version === stat.aktuelle_version ? ' (aktuell)' : ''} · {v.starts} Starts
                    </option>
                  ))}
                </select>
              )}
              <select value={quelle} onChange={(e) => setQuelle(e.target.value)}
                      className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="funnel-quelle">
                <option value="">Alle Quellen</option>
                {(stat.quellen || []).map((q) => (
                  <option key={q.quelle} value={q.quelle}>{q.quelle} · {q.starts}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {stat.starts === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">
              Im gewählten Zeitraum hat noch niemand den Funnel gestartet.
            </p>
          ) : (
            <div className="space-y-3">
              {!stat.aussagekraeftig && (
                <p className="text-xs text-muted-foreground rounded-md bg-muted px-3 py-2">
                  Noch zu wenig Daten für eine Aussage ({stat.starts} von mindestens {stat.mindest_starts} Starts).
                  Der größte Absprung wird erst ab {stat.mindest_starts} Starts markiert.
                </p>
              )}
              {stat.schritte.map((s) => {
                const istStart = s.schritt === 'start';
                const istSchlimmster = stat.aussagekraeftig && s.schritt === maxAbsprung && s.verlust > 0;
                return (
                  <div key={s.schritt} data-testid={`funnel-schritt-${s.schritt}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span className="text-sm font-medium">
                        <span className="text-muted-foreground mr-1.5">{s.nummer}.</span>
                        {s.frage}
                      </span>
                      <span className="text-sm tabular-nums whitespace-nowrap">
                        <b>{s.erreicht}</b>
                        {!istStart && <span className="text-muted-foreground"> · {s.anteil} %</span>}
                      </span>
                    </div>
                    <div className="h-6 rounded-md bg-muted overflow-hidden">
                      <div
                        className={`h-full transition-all ${istStart ? 'bg-foreground/30' : istSchlimmster ? 'bg-marke' : 'bg-marke/45'}`}
                        style={{ width: `${Math.max(s.anteil, 1.5)}%` }}
                      />
                    </div>
                    {/* Absprung-Label UNTER dem Balken, nie darin */}
                    {!istStart && (
                      <p className={`text-xs mt-1 tabular-nums ${istSchlimmster ? 'font-semibold text-marke' : 'text-muted-foreground'}`}>
                        {s.verlust > 0 ? `−${s.verlust} · −${s.verlust_prozent} % zum Vorschritt` : 'kein Verlust zum Vorschritt'}
                        {istSchlimmster && <span className="inline-flex items-center gap-1 ml-2"><TrendingDown className="h-3.5 w-3.5" /> Größter Absprung</span>}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Abbrecher */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Abbrecher mit Telefonnummer
            {abbrecher.length > 0 && <Badge variant="destructive">{abbrecher.length}</Badge>}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Haben ihre Nummer schon dagelassen, aber keinen Termin gewählt — der
            wärmste Anruf, den ihr gerade führen könnt.
          </p>
        </CardHeader>
        <CardContent>
          {abbrecher.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">
              Keine offenen Abbrecher — alle sind durchgelaufen.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Telefon</th>
                    <th className="py-2 pr-3 font-medium">Kam bis</th>
                    <th className="py-2 font-medium">Zuletzt</th>
                  </tr>
                </thead>
                <tbody>
                  {abbrecher.map((s, i) => {
                    const a = s.antworten || {};
                    const name = [a.vorname, a.nachname].filter(Boolean).join(' ')
                      || a.kind_vorname || '(ohne Namen)';
                    const schritte = s.erreichte_schritte || [];
                    return (
                      <tr key={s.token || i} className="border-b last:border-0">
                        <td className="py-2.5 pr-3 font-medium">{name}</td>
                        <td className="py-2.5 pr-3">
                          {a.telefon ? (
                            <a href={`tel:${a.telefon.replace(/\s/g, '')}`}
                               className="text-marke hover:underline inline-flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" /> {a.telefon}
                            </a>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 pr-3 text-muted-foreground">
                          {schritte.length > 0
                            ? `Schritt ${schritte.length} (${schritte[schritte.length - 1]})`
                            : 'Start'}
                        </td>
                        <td className="py-2.5 text-muted-foreground whitespace-nowrap">
                          {fmtZeit(s.zuletzt_am || s.gestartet_am)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------- Seite */

export default function FunnelsPage() {
  const [tage, setTage] = useState(30);
  const [tab, setTab] = useState(FUNNELS[0].slug);
  const [neuLaden, setNeuLaden] = useState(0);
  const aktiv = useMemo(() => FUNNELS.find((f) => f.slug === tab), [tab]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Filter className="h-6 w-6 text-marke" /> Funnels
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Die öffentlichen Probetrainings-Strecken — Zugriff, Conversion und
            die Stellen, an denen Interessenten aussteigen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {ZEITRAEUME.map(([t, label]) => (
            <Button key={t} size="sm" variant={tage === t ? 'default' : 'outline'}
                    onClick={() => setTage(t)}>
              {label}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setNeuLaden((n) => n + 1)}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Aktualisieren
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start">
          {FUNNELS.map((f) => (
            <TabsTrigger key={f.slug} value={f.slug} className="flex-col items-start py-2 px-4">
              <span className="font-medium">{f.label}</span>
              <span className="text-xs text-muted-foreground">{f.beschreibung}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {FUNNELS.map((f) => (
          <TabsContent key={f.slug} value={f.slug} className="mt-4">
            {aktiv?.slug === f.slug && (f.kampagne
              ? <KidsLaunchBlock key={`${f.slug}-${tage}-${neuLaden}`} tage={tage} />
              : <FunnelBlock key={`${f.slug}-${tage}-${neuLaden}`} funnel={f} tage={tage} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
